import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import store from './store';
import Login from './pages/Login';
import Chat from './pages/Chat';
import ChangePassword from './pages/ChangePassword';
import UserManagement from './pages/UserManagement';
import AppSettings from './pages/AppSettings';
import Modal from './components/Modal';
import { checkAuth, getToken } from './utils/auth';
import { checkInitStatus, getCurrentUser } from './services/api';
import { useI18n } from './i18n/I18nContext';
import styles from './App.module.css';

function App() {
  const { t } = useI18n();
  const [initStatus, setInitStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 檢查系統初始化狀態
    checkInitStatus()
      .then(data => {
        setInitStatus(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('檢查初始化狀態失敗:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className={styles.appLoading}>
        <div>{t('app.loading')}</div>
      </div>
    );
  }

  // 如果沒有管理員，顯示錯誤訊息
  if (initStatus && !initStatus.has_admin) {
    return (
      <div className={styles.appError}>
        <h1>{t('app.error_title')}</h1>
        <p>{t('app.error_no_admin_detail')}</p>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <Router>
        <Modal />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route
            path="/chat"
            element={
              <PrivateRoute>
                <Chat />
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <UserManagement />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <AppSettings />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Navigate to="/chat" replace />} />
        </Routes>
      </Router>
    </Provider>
  );
}

function PrivateRoute({ children }) {
  const isAuthenticated = checkAuth();
  const token = getToken();

  if (!isAuthenticated || !token) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  const [state, setState] = useState('loading');
  const { t } = useI18n();

  useEffect(() => {
    getCurrentUser()
      .then((res) => {
        if (res.data?.role === 'admin') {
          setState('ok');
        } else {
          setState('deny');
        }
      })
      .catch(() => setState('deny'));
  }, []);

  if (state === 'loading') {
    return (
      <div className={styles.appLoading}>
        <div>{t('app.loading')}</div>
      </div>
    );
  }

  if (state === 'deny') {
    return <Navigate to="/chat" replace />;
  }

  return children;
}

export default App;
